import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload';
import { useToast } from '../../../shared/components/toast/useToast';
import { storeConfigApi, type BrandAssets } from '../api/storeConfigApi';
import { KeyedImageSlot } from './KeyedImageSlot';

/**
 * Brand Assets (storefront onboarding).
 *
 * Uploads a merchant's brand imagery to GCS via osprey-api `/media/upload` with
 * `x-tenant-type: APP` (media-service routes APP-typed uploads to the platform public
 * bucket and returns a durable `storage.googleapis.com` URL) and PERSISTS the resulting
 * URLs to the merchant's store-config `brandAssets` blob via the admin-by-id
 * business-profile route (PATCH /merchants/:id/business-profile). The storefront reads
 * that blob at runtime and overlays it on its static MerchantConfig — so an upload here
 * changes the live store with no code edit or redeploy (PE brand-assets).
 *
 * Slots map 1:1 onto the storefront config: logo/favicon/social (scalars), hero + community
 * (ordered lists, hero collage uses 3), and per-label collection/occasion tiles. The logo is
 * additionally mirrored to `storeLogoUrl` so existing logo consumers keep working.
 */

type ScalarSlot = 'logoImage' | 'favicon' | 'ogImage' | 'about' | 'story';

interface SlotMeta {
  key: ScalarSlot;
  label: string;
  hint: string;
}

const SCALAR_SLOTS: SlotMeta[] = [
  { key: 'logoImage', label: 'Logo', hint: 'Header/nav mark · transparent PNG or SVG' },
  { key: 'favicon', label: 'Favicon', hint: 'Browser-tab icon · square, ≥ 256px' },
  { key: 'ogImage', label: 'Social preview', hint: 'Open Graph share image · 1200×630' },
  { key: 'about', label: 'About image', hint: 'About page hero · wide, high-res' },
  { key: 'story', label: 'Story image', hint: 'Brand-story section · portrait 3:4' },
];

interface BrandAssetsPanelProps {
  /** Watchman merchant id — the x-tenant-id the asset is isolated under. */
  merchantId: string;
  /** Solution slug — part of the GCS object-key prefix. */
  solutionId: string | null;
}

/** Shallow-normalise a server blob into the editable shape (never null fields). */
function fromServer(b: BrandAssets | null | undefined, logoFallback: string | null): BrandAssets {
  const assets: BrandAssets = { ...(b ?? {}) };
  if (!assets.logoImage && logoFallback) assets.logoImage = logoFallback;
  return assets;
}

export function BrandAssetsPanel({ merchantId, solutionId }: BrandAssetsPanelProps) {
  const { upload } = useMediaUpload();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [assets, setAssets] = useState<BrandAssets>({});
  const [busy, setBusy] = useState<string | null>(null);
  const hydrated = useRef(false);

  const storeConfigKey = ['store-config', merchantId] as const;
  const { data: storeConfig } = useQuery({
    queryKey: storeConfigKey,
    queryFn: () => storeConfigApi.get(merchantId),
    staleTime: 60 * 1000,
    enabled: !!merchantId,
  });

  // Seed the editor from the server once, so a reload / another operator sees the persisted
  // imagery. After hydration, local state is authoritative for this session's edits.
  useEffect(() => {
    if (storeConfig && !hydrated.current) {
      setAssets(fromServer(storeConfig.brandAssets, storeConfig.storeLogoUrl ?? null));
      hydrated.current = true;
    }
  }, [storeConfig]);

  const persistMutation = useMutation({
    mutationFn: (next: BrandAssets) =>
      storeConfigApi.updateBusinessProfile(merchantId, {
        brandAssets: next,
        // Mirror the logo to storeLogoUrl so existing logo consumers keep working.
        ...(next.logoImage ? { storeLogoUrl: next.logoImage } : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: storeConfigKey }),
  });

  /** Optimistically set local state, then persist the whole blob to the server. */
  const persist = useCallback(
    async (next: BrandAssets, successMsg: string) => {
      setAssets(next);
      try {
        await persistMutation.mutateAsync(next);
        toast.success('Saved', successMsg);
      } catch (err) {
        const msg = (err as { message?: string })?.message ?? 'could not save to store config';
        toast.error('Uploaded, not saved', `On GCS, but ${msg}.`);
      }
    },
    [persistMutation, toast],
  );

  const uploadFile = useCallback(
    async (file: File): Promise<string> => {
      const uploaded = await upload(file, solutionId ?? 'rack-and-box', {
        tenantId: merchantId,
        tenantType: 'APP',
      });
      return uploaded.url;
    },
    [upload, solutionId, merchantId],
  );

  // ── Scalar slots ─────────────────────────────────────────────────────────
  const setScalar = useCallback(
    async (slot: ScalarSlot, file: File | undefined) => {
      if (!file) return;
      setBusy(slot);
      try {
        const url = await uploadFile(file);
        await persist({ ...assets, [slot]: url }, `${slot} saved to the store config`);
      } catch (err) {
        toast.error('Upload failed', (err as { message?: string })?.message ?? 'Upload failed');
      } finally {
        setBusy(null);
      }
    },
    [assets, uploadFile, persist, toast],
  );

  const clearScalar = useCallback(
    (slot: ScalarSlot) => {
      const next = { ...assets };
      delete next[slot];
      void persist(next, `${slot} removed`);
    },
    [assets, persist],
  );

  // ── List slots (hero, community) ───────────────────────────────────────────
  const addToList = useCallback(
    async (slot: 'hero' | 'community', file: File | undefined) => {
      if (!file) return;
      setBusy(`${slot}:add`);
      try {
        const url = await uploadFile(file);
        const list = [...(assets[slot] ?? []), url];
        await persist({ ...assets, [slot]: list }, `${slot} image added`);
      } catch (err) {
        toast.error('Upload failed', (err as { message?: string })?.message ?? 'Upload failed');
      } finally {
        setBusy(null);
      }
    },
    [assets, uploadFile, persist, toast],
  );

  const removeFromList = useCallback(
    (slot: 'hero' | 'community', index: number) => {
      const list = (assets[slot] ?? []).filter((_, i) => i !== index);
      const next = { ...assets };
      if (list.length) next[slot] = list;
      else delete next[slot];
      void persist(next, `${slot} image removed`);
    },
    [assets, persist],
  );

  // ── Keyed slots (collections, occasions) ───────────────────────────────────
  const setKeyedImage = useCallback(
    async (slot: 'collections' | 'occasions', label: string, file: File | undefined) => {
      if (!file || !label.trim()) return;
      setBusy(`${slot}:${label}`);
      try {
        const url = await uploadFile(file);
        const rows = [...(assets[slot] ?? [])];
        const idx = rows.findIndex((r) => r.label.trim().toLowerCase() === label.trim().toLowerCase());
        if (idx >= 0) rows[idx] = { label: rows[idx].label, image: url };
        else rows.push({ label: label.trim(), image: url });
        await persist({ ...assets, [slot]: rows }, `${label} tile saved`);
      } catch (err) {
        toast.error('Upload failed', (err as { message?: string })?.message ?? 'Upload failed');
      } finally {
        setBusy(null);
      }
    },
    [assets, uploadFile, persist, toast],
  );

  const removeKeyed = useCallback(
    (slot: 'collections' | 'occasions', label: string) => {
      const rows = (assets[slot] ?? []).filter(
        (r) => r.label.trim().toLowerCase() !== label.trim().toLowerCase(),
      );
      const next = { ...assets };
      if (rows.length) next[slot] = rows;
      else delete next[slot];
      void persist(next, `${label} tile removed`);
    },
    [assets, persist],
  );

  const savedAssets = storeConfig?.brandAssets ?? {};
  const scalarSaved = (slot: ScalarSlot) => !!assets[slot] && assets[slot] === savedAssets[slot];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-1">
        <h2 className="text-base font-semibold text-foreground">Brand Assets</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Upload this merchant's brand imagery. Everything here saves to the store config and appears
          on the live storefront at runtime — no code change or redeploy needed.
        </p>
      </div>

      {/* Scalar slots */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {SCALAR_SLOTS.map((slot) => (
          <BrandAssetSlot
            key={slot.key}
            meta={slot}
            url={assets[slot.key]}
            busy={busy === slot.key || persistMutation.isPending}
            persisted={scalarSaved(slot.key)}
            onSelect={(file) => setScalar(slot.key, file)}
            onClear={() => clearScalar(slot.key)}
          />
        ))}
      </div>

      {/* List slots */}
      <ImageListSlot
        title="Hero"
        hint="Homepage hero collage — the storefront uses the first 3 images."
        urls={assets.hero ?? []}
        busy={busy === 'hero:add'}
        onAdd={(file) => addToList('hero', file)}
        onRemove={(i) => removeFromList('hero', i)}
      />
      <ImageListSlot
        title="Community"
        hint="“Worn by the community” grid — ordered gallery tiles."
        urls={assets.community ?? []}
        busy={busy === 'community:add'}
        onAdd={(file) => addToList('community', file)}
        onRemove={(i) => removeFromList('community', i)}
      />

      {/* Keyed slots */}
      <KeyedImageSlot
        title="Collection tiles"
        hint="Image per collection. The label must match a collection label in the storefront config."
        placeholder="Collection label (e.g. Aso-Oke)"
        rows={assets.collections ?? []}
        busyLabel={busy?.startsWith('collections:') ? busy.slice('collections:'.length) : null}
        onUpload={(label, file) => setKeyedImage('collections', label, file)}
        onRemove={(label) => removeKeyed('collections', label)}
      />
      <KeyedImageSlot
        title="Occasion tiles"
        hint="Image per occasion. The label must match an occasion label in the storefront config."
        placeholder="Occasion label (e.g. Wedding)"
        rows={assets.occasions ?? []}
        busyLabel={busy?.startsWith('occasions:') ? busy.slice('occasions:'.length) : null}
        onUpload={(label, file) => setKeyedImage('occasions', label, file)}
        onRemove={(label) => removeKeyed('occasions', label)}
      />
    </div>
  );
}

// ── Scalar slot ──────────────────────────────────────────────────────────────

interface BrandAssetSlotProps {
  meta: SlotMeta;
  url?: string;
  busy: boolean;
  persisted: boolean;
  onSelect: (file: File | undefined) => void;
  onClear: () => void;
}

function BrandAssetSlot({ meta, url, busy, persisted, onSelect, onClear }: BrandAssetSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="border border-border rounded-[10px] p-3">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-foreground">{meta.label}</div>
            {persisted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                Saved
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{meta.hint}</p>
        </div>
        {url && (
          <button onClick={onClear} className="text-[11px] text-muted-foreground hover:text-red-500" title="Remove">
            Remove
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          onSelect(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {url ? (
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 rounded-[8px] overflow-hidden flex-shrink-0 border border-border bg-muted">
            <img src={url} alt={meta.label} className="w-full h-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-muted-foreground font-mono truncate" title={url}>
              {url}
            </p>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="mt-1.5 px-2.5 py-1 border border-border rounded-md text-[11px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              {busy ? 'Uploading…' : 'Replace'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full border-2 border-dashed border-border rounded-[8px] py-4 text-center text-[12px] text-muted-foreground hover:border-muted-foreground transition-colors disabled:opacity-50"
        >
          {busy ? 'Uploading…' : (
            <>
              <span className="font-medium text-foreground">Click to upload</span> {meta.label.toLowerCase()}
            </>
          )}
        </button>
      )}
    </div>
  );
}

// ── Ordered image list (hero, community) ──────────────────────────────────────

interface ImageListSlotProps {
  title: string;
  hint: string;
  urls: string[];
  busy: boolean;
  onAdd: (file: File | undefined) => void;
  onRemove: (index: number) => void;
}

function ImageListSlot({ title, hint, urls, busy, onAdd, onRemove }: ImageListSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="border border-border rounded-[10px] p-3 mt-4">
      <div className="mb-2">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          onAdd(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div className="flex flex-wrap gap-3">
        {urls.map((url, i) => (
          <div key={`${url}-${i}`} className="relative w-20 h-20 rounded-[8px] overflow-hidden border border-border bg-muted group">
            <img src={url} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" />
            <button
              onClick={() => onRemove(i)}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-20 h-20 border-2 border-dashed border-border rounded-[8px] flex items-center justify-center text-[11px] text-muted-foreground hover:border-muted-foreground transition-colors disabled:opacity-50"
        >
          {busy ? '…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

// KeyedImageSlot (collection/occasion tiles) is now shared — see ./KeyedImageSlot.
